import React, { useEffect, useState, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { get, post, put, del } from './api';
import { t, LanguageSwitcher } from '@omnifuse/shared/frontend/i18n';
import { SettingsPanel } from '@omnifuse/shared/frontend/SettingsPanel';
import { get as apiGet, post as apiPost, put as apiPut } from './api';

const S = {
  page: { maxWidth: 1150, margin: '0 auto', padding: 24 } as React.CSSProperties,
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 16 } as React.CSSProperties,
  btn: { padding: '6px 14px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  btnPrimary: { padding: '6px 14px', borderRadius: 8, border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', fontSize: 13 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: { textAlign: 'left' as const, padding: '8px 10px', borderBottom: '2px solid #eee', color: '#555', whiteSpace: 'nowrap' as const },
  td: { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' },
  tab: (a: boolean) => ({ padding: '8px 18px', cursor: 'pointer', borderBottom: a ? '2px solid #1a73e8' : '2px solid transparent', color: a ? '#1a73e8' : '#555', fontWeight: a ? 600 : 400, fontSize: 14 }) as React.CSSProperties,
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 } as React.CSSProperties,
  bar: (pct: number, color: string) => ({
    height: 14, width: `${Math.max(2, pct)}%`, background: color, borderRadius: 4, transition: 'width .3s',
  }) as React.CSSProperties,
};

// ═══ SoV 仪表盘 ═══
function SovView() {
  const [sov, setSov] = useState<any>(null);
  const [gaps, setGaps] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);

  useEffect(() => {
    get('/api/v1/sov').then((r: any) => setSov(r.data)).catch(() => {});
    get('/api/v1/sov/gap-queries').then((r: any) => setGaps(r.data || [])).catch(() => {});
    get('/api/v1/alerts').then((r: any) => setAlerts(r.data || [])).catch(() => {});
  }, []);

  if (!sov) return <div style={S.card}>{t('加载中...（先在"监测配置"添加查询词并运行探测）')}</div>;

  return (
    <>
      {alerts.filter((a: any) => !a.is_read).slice(0, 3).map((a: any) => (
        <div key={a.id} style={{ ...S.card, background: a.severity === 'critical' ? '#fce8e6' : '#fef7e0', padding: 12 }}>
          ⚠️ {a.message}
        </div>
      ))}
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('AI Share-of-Voice（30 天）')}</h3>
        {(sov.brands || []).map((b: any) => (
          <div key={b.brand} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 140, fontSize: 13, fontWeight: b.is_own ? 700 : 400 }}>
              {b.is_own ? '⭐ ' : ''}{b.brand}
            </div>
            <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 4 }}>
              <div style={S.bar(b.share_of_voice, b.is_own ? '#1a73e8' : '#bbb')} />
            </div>
            <div style={{ width: 200, fontSize: 12, color: '#666' }}>
              SoV {b.share_of_voice}% · 提及率 {b.mention_rate}%{b.avg_position ? ` · 平均第 ${b.avg_position} 位` : ''}
            </div>
          </div>
        ))}
        {(!sov.brands || !sov.brands.length) && <div style={{ color: '#888' }}>{t('暂无数据 — 运行探测后查看')}</div>}
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('分引擎表现（自有品牌提及率）')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('引擎')}</th><th style={S.th}>{t('探测次数')}</th><th style={S.th}>{t('提及率')}</th></tr></thead>
          <tbody>
            {(sov.by_engine || []).map((e: any) => (
              <tr key={e.engine}><td style={S.td}>{e.engine}</td><td style={S.td}>{e.probes}</td><td style={S.td}>{e.mention_rate}%</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('差距查询词（竞品被推荐而你没有）')}</h3>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('查询词')}</th><th style={S.th}>{t('引擎')}</th><th style={S.th}>{t('被推荐的竞品')}</th></tr></thead>
          <tbody>
            {gaps.map((g: any, i: number) => (
              <tr key={i}><td style={S.td}>{g.query_text}</td><td style={S.td}>{g.engine}</td><td style={{ ...S.td, color: '#d93025' }}>{g.competitors_mentioned}</td></tr>
            ))}
            {gaps.length === 0 && <tr><td style={S.td} colSpan={3}>{t('暂无差距查询词 🎉')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ 监测配置 ═══
function ConfigView() {
  const [queries, setQueries] = useState<any[]>([]);
  const [competitors, setCompetitors] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>({ config: {} });
  const [qText, setQText] = useState('');
  const [cName, setCName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    get('/api/v1/queries').then((r: any) => setQueries(r.data || [])).catch(() => {});
    get('/api/v1/competitors').then((r: any) => setCompetitors(r.data || [])).catch(() => {});
    get('/api/v1/settings').then((r: any) => setSettings({ ...r.data, config: JSON.parse(r.data.config_json || '{}') })).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const saveBrand = async () => {
    try { await put('/api/v1/settings', { plan_tier: settings.plan_tier, config: settings.config }); alert('已保存'); }
    catch (e: any) { alert(e.message); }
  };

  const runProbe = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/probes/run', {}); alert(`探测完成：${r.data.probes} 次探测，解析 ${r.data.mentions_parsed} 条提及`); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>{t('品牌设置')}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...S.input, width: 260 }} placeholder={t('品牌名（用于提及识别）')}
            value={settings.config?.brand_name || ''}
            onChange={e => setSettings({ ...settings, config: { ...settings.config, brand_name: e.target.value } })} />
          <button style={S.btnPrimary} onClick={saveBrand}>{t('保存')}</button>
          <button style={{ ...S.btnPrimary, background: '#188038' }} disabled={busy} onClick={runProbe}>
            {busy ? '探测中（约 1-2 分钟）...' : t('🚀 立即运行探测')}
          </button>
        </div>
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>监测查询词（{queries.length}）</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={{ ...S.input, flex: 1 }} placeholder={t('如 "best portable blender under $100"')} value={qText} onChange={e => setQText(e.target.value)} />
          <button style={S.btnPrimary} onClick={async () => { if (!qText) return; await post('/api/v1/queries', { query_text: qText }); setQText(''); load(); }}>{t('添加')}</button>
        </div>
        {queries.map((q: any) => (
          <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
            <span>{q.query_text}</span>
            <button style={S.btn} onClick={async () => { await del(`/api/v1/queries/${q.id}`); load(); }}>{t('删除')}</button>
          </div>
        ))}
      </div>
      <div style={S.card}>
        <h3 style={{ fontSize: 15, marginBottom: 12 }}>竞品品牌（{competitors.length}）</h3>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input style={{ ...S.input, width: 260 }} placeholder={t('竞品品牌名')} value={cName} onChange={e => setCName(e.target.value)} />
          <button style={S.btnPrimary} onClick={async () => { if (!cName) return; await post('/api/v1/competitors', { brand_name: cName }); setCName(''); load(); }}>{t('添加')}</button>
        </div>
        {competitors.map((cb: any) => (
          <div key={cb.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f5f5f5', fontSize: 13 }}>
            <span>{cb.brand_name}</span>
            <button style={S.btn} onClick={async () => { await del(`/api/v1/competitors/${cb.id}`); load(); }}>{t('删除')}</button>
          </div>
        ))}
      </div>
    </>
  );
}

// ═══ 探测记录 ═══
function ProbesView() {
  const [rows, setRows] = useState<any[]>([]);
  const [detail, setDetail] = useState<any>(null);

  useEffect(() => { get('/api/v1/probes').then((r: any) => setRows(r.data || [])).catch(() => {}); }, []);

  const open = async (id: string) => {
    try { const r: any = await get(`/api/v1/probes/${id}`); setDetail(r.data); } catch (e: any) { alert(e.message); }
  };

  return (
    <>
      {detail && (
        <div style={{ ...S.card, background: '#f8f9fa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <strong>{detail.engine} · {detail.query_text || ''}</strong>
            <button style={S.btn} onClick={() => setDetail(null)}>{t('关闭')}</button>
          </div>
          <div style={{ marginBottom: 10 }}>
            {(detail.mentions || []).filter((m: any) => m.mentioned).map((m: any) => (
              <span key={m.id} style={{ display: 'inline-block', margin: '0 8px 6px 0', padding: '2px 10px', borderRadius: 12, fontSize: 12, background: m.is_own_brand ? '#e8f0fe' : '#f1f3f4' }}>
                {m.brand}{m.position ? ` #${m.position}` : ''} · {m.sentiment}
              </span>
            ))}
          </div>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, maxHeight: 300, overflow: 'auto' }}>{detail.response_text}</pre>
        </div>
      )}
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>{t('查询词')}</th><th style={S.th}>{t('引擎')}</th><th style={S.th}>{t('提及品牌数')}</th><th style={S.th}>{t('状态')}</th><th style={S.th}>{t('时间')}</th></tr></thead>
          <tbody>
            {rows.map((r: any) => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => open(r.id)}>
                <td style={S.td}>{r.query_text}</td><td style={S.td}>{r.engine}</td>
                <td style={S.td}>{r.brands_mentioned}</td><td style={S.td}>{r.status}</td>
                <td style={S.td}>{(r.probed_at || '').slice(0, 16)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td style={S.td} colSpan={5}>{t('暂无探测记录')}</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══ AI 渠道归因 ═══
function AttributionView() {
  const [stats, setStats] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);

  const load = () => get('/api/v1/attribution/stats').then((r: any) => setStats(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setBusy(true);
    try { const r: any = await post('/api/v1/attribution/scan'); alert(`扫描 ${r.data.orders_scanned} 单，识别 ${r.data.ai_attributed} 单 AI 渠道来源`); load(); }
    catch (e: any) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15 }}>{t('AI 渠道订单归因')}</h3>
        <button style={S.btnPrimary} disabled={busy} onClick={scan}>{busy ? '扫描中...' : t('🔍 扫描近 30 天订单')}</button>
      </div>
      <table style={S.table}>
        <thead><tr><th style={S.th}>{t('AI 渠道')}</th><th style={S.th}>{t('订单数')}</th><th style={S.th}>GMV</th></tr></thead>
        <tbody>
          {stats.map((s: any) => (
            <tr key={s.referrer_source}>
              <td style={S.td}>{s.referrer_source}</td><td style={S.td}>{s.orders}</td>
              <td style={{ ...S.td, fontWeight: 700 }}>${(s.gmv || 0).toFixed(0)}</td>
            </tr>
          ))}
          {stats.length === 0 && <tr><td style={S.td} colSpan={3}>{t('暂无 AI 渠道订单')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ═══ 主应用 ═══
function App() {
  const [tab, setTab] = useState('sov');
  return (
    <div style={S.page}>
      <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 1000 }}><LanguageSwitcher /></div>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>AIVisibilityTracker</h1>
      <p style={{ color: '#666', marginBottom: 16 }}>{t('AI 渠道可见性追踪 — Share-of-Voice · 竞品情报 · 渠道归因')}</p>
      <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: 20 }}>
        <div style={S.tab(tab === 'sov')} onClick={() => setTab('sov')}>{t('SoV 仪表盘')}</div>
        <div style={S.tab(tab === 'config')} onClick={() => setTab('config')}>{t('监测配置')}</div>
        <div style={S.tab(tab === 'probes')} onClick={() => setTab('probes')}>{t('探测记录')}</div>
        <div style={S.tab(tab === 'attribution')} onClick={() => setTab('attribution')}>{t('渠道归因')}</div>
        <div style={S.tab(tab === 'settings')} onClick={() => setTab('settings')}>{t('设置')}</div>
      </div>
      {tab === 'sov' && <SovView />}
      {tab === 'config' && <ConfigView />}
      {tab === 'probes' && <ProbesView />}
      {tab === 'attribution' && <AttributionView />}
      {tab === 'settings' && <SettingsPanel api={{ get: apiGet, post: apiPost, put: apiPut }} />}
    </div>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(<App />);
